import React from 'react';
import { Wifi, WifiOff, AlertTriangle, Info, CheckCircle, Clock } from 'lucide-react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

// Network Status Bar Component
export const NetworkStatusBar: React.FC = () => {
  const { isOnline, connectionQuality, timeSinceLastOnline } = useNetworkStatus();

  if (isOnline && connectionQuality === 'good') return null;

  return (
    <div className={`w-full py-2 px-4 text-center text-sm font-medium ${
      !isOnline 
        ? 'bg-red-600 text-white' 
        : connectionQuality === 'poor'
        ? 'bg-amber-500 text-white'
        : 'bg-blue-600 text-white'
    }`}>
      <div className="flex items-center justify-center space-x-2">
        {!isOnline ? (
          <>
            <WifiOff size={16} />
            <span>
              You are offline. {timeSinceLastOnline && `Last online: ${timeSinceLastOnline}.`} Some features may not work properly.
            </span>
          </>
        ) : connectionQuality === 'poor' ? (
          <>
            <AlertTriangle size={16} />
            <span>Slow connection detected. Some features may be slower than usual.</span>
          </>
        ) : (
          <>
            <Wifi size={16} />
            <span>Limited connectivity. You may experience some delays.</span>
          </>
        )}
      </div>
    </div>
  );
};

// Form Submission Status Component
export const FormStatus: React.FC<{
  status: 'idle' | 'submitting' | 'success' | 'error';
  message?: string;
  className?: string;
}> = ({ status, message, className = '' }) => {
  if (status === 'idle') return null;

  const statusConfig = {
    submitting: {
      icon: <Clock className="text-blue-500 animate-spin" size={18} />,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-800',
      borderColor: 'border-blue-200',
      defaultMessage: 'Submitting your information...'
    },
    success: {
      icon: <CheckCircle className="text-green-500" size={18} />,
      bgColor: 'bg-green-50',
      textColor: 'text-green-800',
      borderColor: 'border-green-200',
      defaultMessage: 'Submitted successfully!'
    },
    error: {
      icon: <AlertTriangle className="text-red-500" size={18} />,
      bgColor: 'bg-red-50',
      textColor: 'text-red-800',
      borderColor: 'border-red-200',
      defaultMessage: 'There was an error. Please try again.'
    }
  };

  const { icon, bgColor, textColor, borderColor, defaultMessage } = statusConfig[status];
  const displayMessage = message || defaultMessage;

  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 ${className}`}>
      <div className="flex items-center space-x-2">
        {icon}
        <span className={`text-sm ${textColor}`}>{displayMessage}</span>
      </div>
    </div>
  );
};

// System Status Banner
export const SystemStatusBanner: React.FC<{
  type: 'maintenance' | 'outage' | 'update' | 'announcement';
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
  className?: string;
}> = ({ type, message, action, onDismiss, className = '' }) => {
  const statusConfig = {
    maintenance: {
      icon: <Clock className="text-amber-500" size={18} />,
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-800',
      borderColor: 'border-amber-200',
    },
    outage: {
      icon: <AlertTriangle className="text-red-500" size={18} />,
      bgColor: 'bg-red-50',
      textColor: 'text-red-800',
      borderColor: 'border-red-200',
    },
    update: {
      icon: <Info className="text-blue-500" size={18} />,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-800',
      borderColor: 'border-blue-200',
    },
    announcement: {
      icon: <Info className="text-green-500" size={18} />,
      bgColor: 'bg-green-50',
      textColor: 'text-green-800',
      borderColor: 'border-green-200',
    }
  };

  const { icon, bgColor, textColor, borderColor } = statusConfig[type];

  return (
    <div className={`${bgColor} ${borderColor} border-l-4 p-4 ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {icon}
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm ${textColor}`}>{message}</p>
          {(action || onDismiss) && (
            <div className="mt-2 flex">
              {action && (
                <button
                  type="button"
                  onClick={action.onClick}
                  className={`mr-2 text-sm font-medium ${textColor} hover:underline focus:outline-none`}
                >
                  {action.label}
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Save Status Indicator
export const SaveStatus: React.FC<{
  status: 'saved' | 'saving' | 'unsaved' | 'error';
  lastSaved?: Date;
  className?: string;
}> = ({ status, lastSaved, className = '' }) => {
  const statusConfig = {
    saved: {
      icon: <CheckCircle className="text-green-500" size={14} />,
      text: 'Saved',
      textColor: 'text-green-600',
    },
    saving: {
      icon: <Clock className="text-blue-500 animate-spin" size={14} />,
      text: 'Saving...',
      textColor: 'text-blue-600',
    },
    unsaved: {
      icon: <Info className="text-amber-500" size={14} />,
      text: 'Unsaved changes',
      textColor: 'text-amber-600',
    },
    error: {
      icon: <AlertTriangle className="text-red-500" size={14} />,
      text: 'Failed to save',
      textColor: 'text-red-600',
    }
  };

  const { icon, text, textColor } = statusConfig[status];
  
  const formattedTime = lastSaved ? 
    lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
    '';

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      {icon}
      <span className={`text-xs ${textColor}`}>
        {text} {status === 'saved' && lastSaved && `at ${formattedTime}`}
      </span>
    </div>
  );
};

// Action Result Indicator
export const ActionResult: React.FC<{
  success: boolean;
  message: string;
  onDismiss?: () => void;
  className?: string;
}> = ({ success, message, onDismiss, className = '' }) => {
  const icon = success ? 
    <CheckCircle className="text-green-500" size={16} /> : 
    <AlertTriangle className="text-red-500" size={16} />;
  
  const bgColor = success ? 'bg-green-50' : 'bg-red-50';
  const borderColor = success ? 'border-green-200' : 'border-red-200';
  const textColor = success ? 'text-green-800' : 'text-red-800';

  return (
    <div className={`${bgColor} ${borderColor} border rounded-md p-3 ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {icon}
        </div>
        <div className="ml-3 flex-1">
          <p className={`text-sm ${textColor}`}>{message}</p>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="ml-auto -mx-1.5 -my-1.5 bg-green-50 text-green-500 rounded-lg focus:ring-2 focus:ring-green-400 p-1.5 hover:bg-green-200 inline-flex h-8 w-8 items-center justify-center"
          >
            <span className="sr-only">Dismiss</span>
            <svg className="h-3 w-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};